import { getRectOfNodes } from '../geometry';
import type { Dimensions, Node, Viewport } from '../types';
import { DEFAULT_BOUNDS_PADDING, getTransformForBounds } from './transformForBounds';

/** Options of `fitView()` the app uses, plus the v11 ones that change which nodes count. */
export type FitViewOptions = {
  padding?: number;
  includeHiddenNodes?: boolean;
  minZoom?: number;
  maxZoom?: number;
};

export type ZoomLimits = { minZoom: number; maxZoom: number };

const isMeasured = (node: Readonly<Node>): boolean => Boolean(node.width && node.height);

/**
 * Viewport that shows every visible node in a pane of `paneSize`, or `null` when there is nothing
 * to fit or some node is not measured yet (v11 waits for measured sizes before fitting).
 */
export const getFitViewport = (
  nodes: readonly Node[],
  paneSize: Dimensions,
  limits: ZoomLimits,
  { padding = DEFAULT_BOUNDS_PADDING, includeHiddenNodes = false, minZoom, maxZoom }: FitViewOptions = {},
): Viewport | null => {
  const candidates = includeHiddenNodes ? nodes : nodes.filter((node) => !node.hidden);
  if (candidates.length === 0 || !candidates.every(isMeasured)) return null;

  const [x, y, zoom] = getTransformForBounds(
    getRectOfNodes(candidates),
    paneSize.width,
    paneSize.height,
    minZoom ?? limits.minZoom,
    maxZoom ?? limits.maxZoom,
    padding,
  );
  return { x, y, zoom };
};
